import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useAuthStore } from "@/stores/useAuthStore";
import type { UserProfile } from "@/lib/types";
import type { ProfileImageKind } from "@/lib/profileImages";
import { profileKey } from "@/lib/userQueryKeys";

/**
 * `opts.enabled: false` suppresses the fetch entirely.
 *
 * Needed because omitting `userId` falls back to the session user, so a caller
 * that only *sometimes* wants a profile cannot express that by passing
 * `undefined` — it would fetch the viewer's own instead. DEV-792 has exactly
 * that shape: the quest page resolves the creator's display name only when an
 * admin is editing someone else's quest, and must not add a profile request to
 * every other viewer's page load.
 */
export function useProfile(userId?: string, opts?: { enabled?: boolean }) {
  const { users } = useServices();
  const sessionUserId = useAuthStore((s) => s.session?.userId);
  const resolvedId = userId ?? sessionUserId;

  return useQuery({
    queryKey: profileKey(resolvedId, sessionUserId),
    queryFn: () => users.getProfile(resolvedId!),
    enabled: !!resolvedId && (opts?.enabled ?? true),
  });
}

export function useUpdateProfile() {
  const { users } = useServices();
  const userId = useAuthStore((s) => s.session?.userId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<UserProfile>) =>
      users.updateProfile(userId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey(userId, userId) });
    },
  });
}

/**
 * Set or clear the member's profile picture or banner. `file: null` clears.
 * On success the cache is written with the returned row, so the new picture
 * shows without waiting on a refetch.
 */
export function useProfileImage(kind: ProfileImageKind) {
  const { users } = useServices();
  const userId = useAuthStore((s) => s.session?.userId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File | null) =>
      file ? users.setProfileImage(userId!, kind, file) : users.clearProfileImage(userId!, kind),
    onSuccess: (profile) => {
      queryClient.setQueryData(profileKey(userId, userId), profile);
      queryClient.invalidateQueries({ queryKey: profileKey(userId, userId) });
    },
  });
}
